import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Heart, Users, Zap, Star, Trophy, Flame, Crown, Gem, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getIcon(iconName) {
  if (iconName === 'Heart') return Heart;
  if (iconName === 'Users') return Users;
  if (iconName === 'Zap') return Zap;
  if (iconName === 'Star') return Star;
  if (iconName === 'Trophy') return Trophy;
  if (iconName === 'Flame') return Flame;
  if (iconName === 'Crown') return Crown;
  if (iconName === 'Gem') return Gem;
  return Star;
}

function BadgeDisplay({ badges, earnedBadges }) {
  if (!badges || badges.length === 0) return null;
  
  return (
    <div className="flex gap-2 flex-wrap">
      {badges.map(badge => {
        const earned = earnedBadges ? earnedBadges.includes(badge.id) : false;
        const Icon = getIcon(badge.icon);
        return (
          <div
            key={badge.id}
            className={`p-2 rounded-full ${earned ? 'bg-secondary' : 'bg-muted opacity-30'}`}
            title={badge.name}
          >
            <Icon className={`h-5 w-5 ${earned ? badge.color : 'text-muted-foreground'}`} />
          </div>
        );
      })}
    </div>
  );
}

function SkillCard({ skill, progress, onStart }) {
  const Icon = getIcon(skill.icon);
  const completedCount = progress || 0;
  
  return (
    <div className={`card-soft ${skill.color}`} data-testid={`skill-card-${skill.id}`}>
      <div className="flex items-start gap-4">
        <div className="p-3 bg-white rounded-2xl shadow-sm">
          <Icon className={`h-6 w-6 ${skill.iconColor}`} />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-foreground">{skill.name}</h3>
          <p className="text-sm text-muted-foreground mt-1">{skill.description}</p>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{completedCount} exercises completed</span>
            <Button size="sm" onClick={() => onStart(skill.id)} className="rounded-full" data-testid={`practice-${skill.id}-btn`}>
              Practice <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SkillsHub() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState({ total_exercises: 0, skills: {}, badges: [], streak: 0 });
  const [skillCategories, setSkillCategories] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesRes = await fetch('/data/skillCategories.json').then(r => r.json());
        setSkillCategories(categoriesRes.categories || []);
        setBadges(categoriesRes.badges || []);
        
        try {
          const statsRes = await axios.get(`${API}/skills/stats`, { headers: { Authorization: `Bearer ${token}` } });
          setStats(statsRes.data);
        } catch (e) {
          setStats({ total_exercises: 0, skills: {}, badges: [], streak: 0 });
        }
      } catch (err) {
        console.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleStartSkill = (skillId) => {
    navigate(`/skills/${skillId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-safe" data-testid="skills-hub">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" /><span>Back</span>
        </button>

        <div className="mt-6 page-enter">
          <h1 className="font-heading text-3xl font-semibold text-foreground">Skills & Learning</h1>
          <p className="mt-2 text-muted-foreground">Build skills through guided practice</p>
        </div>

        {/* Stats Card */}
        <div className="mt-6 card-soft bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 page-enter stagger-1" data-testid="stats-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Exercises</p>
              <p className="text-3xl font-heading font-bold text-primary" data-testid="total-exercises">{stats.total_exercises}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Current Streak</p>
              <div className="flex items-center gap-1 justify-end">
                <Flame className="h-5 w-5 text-orange-500" />
                <span className="text-2xl font-heading font-bold text-foreground" data-testid="streak-count">{stats.streak}</span>
                <span className="text-sm text-muted-foreground">days</span>
              </div>
            </div>
          </div>
          
          <div>
            <p className="text-sm text-muted-foreground mb-2">Badges Earned</p>
            <BadgeDisplay badges={badges} earnedBadges={stats.badges} />
          </div>
        </div>

        {/* Skill Categories */}
        <div className="mt-8">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4 page-enter stagger-2">Practice Areas</h2>
          
          <div className="space-y-4">
            {skillCategories.map((skill) => (
              <div key={skill.id}>
                <SkillCard 
                  skill={skill} 
                  progress={stats.skills ? stats.skills[skill.id] : 0} 
                  onStart={handleStartSkill} 
                />
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-2 gap-3 page-enter stagger-4">
          <button
            onClick={() => navigate('/journal')}
            className="card-soft text-left p-4 hover:border-primary/30"
            data-testid="journal-quick-link"
          >
            <Heart className="h-5 w-5 text-rose-500 mb-2" />
            <h3 className="font-medium text-foreground text-sm">Journal</h3>
            <p className="text-xs text-muted-foreground">Reflect & process</p>
          </button>
          <button
            onClick={() => navigate('/symptoms')}
            className="card-soft text-left p-4 hover:border-primary/30"
            data-testid="symptoms-quick-link"
          >
            <Zap className="h-5 w-5 text-amber-500 mb-2" />
            <h3 className="font-medium text-foreground text-sm">Symptoms</h3>
            <p className="text-xs text-muted-foreground">Track patterns</p>
          </button>
        </div>

        <div className="h-8" />
      </div>
    </div>
  );
}
